import * as extension from "@stellar/freighter-api";
import { Networks, StrKey } from "@stellar/stellar-sdk";
import type { WalletPort } from "./wallet";

const CHAIN = "stellar:testnet";
// WalletConnect project IDs are public. Keep the existing StealthOS ID as a
// fallback for Vercel builds that don't inject the optional env variable.
const PROJECT_ID =
  process.env.REACT_APP_WALLETCONNECT_PROJECT_ID ||
  "530fc0f7c43eaf9b6105b2b79385aba7";

type WalletConnectSession = {
  namespaces?: {
    stellar?: { accounts?: string[]; methods?: string[]; chains?: string[] };
  };
};

type MobileConnection = {
  provider: Awaited<
    ReturnType<
      typeof import("@walletconnect/universal-provider").UniversalProvider.init
    >
  >;
  modal: ReturnType<typeof import("@reown/appkit/core").createAppKit>;
};

let mobileConnection: Promise<MobileConnection> | null = null;
let selectedMobile = false;

/** Freighter Mobile uses WalletConnect; the desktop extension remains unchanged. */
function useMobile(): boolean {
  return (
    selectedMobile ||
    (typeof navigator !== "undefined" &&
      /android|iphone|ipad|ipod/i.test(navigator.userAgent))
  );
}

async function connectProvider(): Promise<MobileConnection> {
  if (!PROJECT_ID) throw new Error("Mobile wallet pairing is not configured.");
  if (!mobileConnection) {
    mobileConnection = (async () => {
      const [{ UniversalProvider }, { createAppKit }, { mainnet }] =
        await Promise.all([
          import("@walletconnect/universal-provider"),
          import("@reown/appkit/core"),
          import("@reown/appkit/networks"),
        ]);
      const provider = await UniversalProvider.init({
        projectId: PROJECT_ID,
        metadata: {
          name: "StealthOS",
          description: "Stellar Testnet private payments",
          url: window.location.origin,
          icons: [new URL("/favicon.ico", window.location.origin).href],
        },
      });
      // AppKit needs a placeholder network; manual control keeps every
      // request on Stellar Testnet instead of attempting EVM chain switching.
      const modal = createAppKit({
        projectId: PROJECT_ID,
        networks: [mainnet],
        universalProvider: provider,
        manualWCControl: true,
        featuredWalletIds: [
          "997a355c8f682468706a76cff1b004a7115f505fb962dac54b6e9b442dd1c380",
        ],
      });
      const notify = () => window.dispatchEvent(new Event("focus"));
      provider.on("session_delete", notify);
      provider.on("session_expire", notify);
      provider.on("accountsChanged", notify);
      return { provider, modal };
    })().catch((error) => {
      mobileConnection = null;
      throw error;
    });
  }
  return mobileConnection;
}

/** Fail closed if a paired wallet cannot sign both deposits and vault unlocks. */
function approvedAddress(
  session: WalletConnectSession | null | undefined,
): string {
  const stellar = session?.namespaces?.stellar;
  if (
    !stellar?.methods?.includes("stellar_signXDR") ||
    !stellar.methods.includes("stellar_signMessage")
  ) {
    throw new Error(
      "Freighter Mobile must approve transaction and message signing.",
    );
  }
  const accounts = (stellar.accounts || []).filter((account) =>
    account.startsWith(`${CHAIN}:`),
  );
  const address = accounts[0]?.slice(CHAIN.length + 1);
  if (!address || !StrKey.isValidEd25519PublicKey(address)) {
    throw new Error(
      "Connect Freighter Mobile using a Stellar Testnet account.",
    );
  }
  return address;
}

async function mobileAddress(): Promise<string> {
  const { provider } = await connectProvider();
  if (!provider.session)
    throw new Error("Freighter Mobile session ended. Reconnect.");
  return approvedAddress(provider.session);
}

async function validateSigner(options?: {
  address?: string;
  networkPassphrase?: string;
}): Promise<{ address: string; provider: MobileConnection["provider"] }> {
  if (options?.networkPassphrase !== Networks.TESTNET) {
    throw new Error("Select Stellar Testnet in Freighter Mobile.");
  }
  const { provider } = await connectProvider();
  const address = approvedAddress(provider.session);
  if (!options?.address || options.address !== address) {
    throw new Error(
      "Freighter Mobile account changed. Reconnect before signing.",
    );
  }
  return { address, provider };
}

/** One signing interface for desktop Freighter and the Freighter Mobile browser. */
export const freighterTransport: WalletPort & {
  signTransaction: typeof extension.signTransaction;
  signMessage: typeof extension.signMessage;
  disconnectSession(): Promise<void>;
} = {
  async isConnected() {
    if (useMobile()) {
      return PROJECT_ID
        ? { isConnected: true }
        : { isConnected: false, error: "Missing WalletConnect project ID" };
    }
    return extension.isConnected();
  },

  async requestAccess() {
    if (!useMobile()) return extension.requestAccess();
    selectedMobile = true;
    const { provider, modal } = await connectProvider();
    if (provider.session) return { address: approvedAddress(provider.session) };
    await modal.open();
    try {
      const session = await provider.connect({
        namespaces: {
          stellar: {
            methods: ["stellar_signXDR", "stellar_signMessage"],
            chains: [CHAIN],
            events: ["accountsChanged"],
          },
        },
      });
      return { address: approvedAddress(session) };
    } finally {
      // Closing the picker is best-effort; never replace the signing error.
      void modal.close().catch(() => {});
    }
  },

  async getAddress() {
    if (!useMobile()) return extension.getAddress();
    try {
      return { address: await mobileAddress() };
    } catch (error) {
      return { error };
    }
  },

  async getNetworkDetails() {
    if (!useMobile()) return extension.getNetworkDetails();
    try {
      await mobileAddress();
      return { networkPassphrase: Networks.TESTNET };
    } catch (error) {
      return { error };
    }
  },

  async signTransaction(xdr, options) {
    if (!useMobile()) return extension.signTransaction(xdr, options);
    const { address, provider } = await validateSigner(options);
    const signed = (await provider.request(
      { method: "stellar_signXDR", params: { xdr } },
      CHAIN,
    )) as { signedXDR?: string };
    if (!signed?.signedXDR)
      throw new Error("Freighter Mobile did not sign the deposit.");
    return { signedTxXdr: signed.signedXDR, signerAddress: address };
  },

  async signMessage(message, options) {
    if (!useMobile()) return extension.signMessage(message, options);
    const { address, provider } = await validateSigner(options);
    const signed = (await provider.request(
      { method: "stellar_signMessage", params: { message } },
      CHAIN,
    )) as { signature?: string };
    if (!signed?.signature)
      throw new Error("Freighter Mobile did not sign the message.");
    return { signedMessage: signed.signature, signerAddress: address };
  },

  async disconnectSession() {
    if (!useMobile() || !mobileConnection) return;
    const { provider } = await mobileConnection;
    if (provider.session) await provider.disconnect();
  },
};
